import { useEffect, useMemo, useState } from 'react';
import type { Device, ScheduleType } from '@cornerstone/shared';
import { devicePowerBlockReason } from '@cornerstone/shared';
import { API_BASE } from '../client/config';
import { api } from '../client/api';
import { playerPreviewUrl } from '../client/previewUrl';
import { SECTION_BODY_GUTTER } from '../nav/PageContainer';
import {
  createDeviceGroupsStore, useDeviceGroupsStore,
  type ApprovalMode, type DeviceGroup, type GroupActor, type GroupBranding, type GroupDevice,
} from '../store/deviceGroups';
import { Button, Field, Panel, SearchInput } from '../components/ui';
import { GroupScopeSetup } from '../components/GroupScopeSetup';
import { NO_GROUP_TITLES, NoAssignedGroupsNotice, hasNoAssignedGroups } from '../components/GroupAssignmentNotice';
import { ConfirmModal, InfoTooltip, MenuDivider, MenuRow, ModalShell, ToolbarMenu } from '../components/menu';
import { ContentSkeleton } from '../components/skeleton/ContentSkeleton';
import { DeviceDiagnosticsBadge, useDeviceDiagnostics, type DiagnosticStatus } from '../components/DeviceDiagnostics';
import { DeviceCodeClaimButton } from '../components/DeviceCodeClaim';
import { NewDevicePairing, canPair, pairingGroups } from '../components/DevicePairing';
import { DeviceDetailModal } from '../components/DeviceDetailModal';
import { DeviceRuntimeBadge, Icon } from '../components/DeviceRuntime';
import { showsPowerCard } from './DeviceDetail';
import { describeApiError, toast } from '../components/toast';
import { APPROVAL_MODE_LABELS, ROLE_LABELS } from '../labels/orgConcepts';
import { useWriteGate, writeGate, type WriteGate } from '../nav/useWriteGate';
import { cn } from '../utils/cn';

// =============================================================================
// 구성 관리 > 그룹 · 디바이스 — 그룹과 디바이스를 한 화면에서 (2026-09-14, 대교 결정 ⓐ).
//
//   [검색 · 상태]                          [새로고침] [기기 코드로 연결] [+ 디바이스 추가] [+ 그룹 만들기]
//   ┌ 그룹 목록 ─────────┐ ┌ 고른 그룹의 디바이스 ─────────────────────────────────────────┐
//   │ 전체 (n)           │ │ 그룹 이름 · 승인 방식 · 담당 n            [수정] [정리]         │
//   │ ● 그룹 A  기기 5   │ │ 상태 · 디바이스명 · ID · 유형 · 그룹(이동) · 해상도 · 타임라인 · 액션 │
//   │ ● 그룹 B  기기 3   │ │                                                              │
//   │ 미배정 (2) ⚠       │ │                                                              │
//   └────────────────────┘ └──────────────────────────────────────────────────────────────┘
//
// 데이터는 /api/device-groups 스코프 하나(그룹·디바이스·actor)로 — 서버가 역할에 맞게 이미 깎아 준다
// (그룹 담당자는 담당 그룹의 기기만). 상태는 진단 API, 타임라인은 스케줄 타입 목록에서.
// 역할: Owner·중앙 관리자 = 그룹 CRUD·전체 배정·페어링. 그룹 관리자 = 담당 그룹 안에서 배정·페어링.
//       편집자·조회자 = 읽기 + 미리보기. 삭제는 Owner(또는 고객사 상세를 보는 운영사)만 — 서버 규칙과 같음.
// 이전 '디바이스' 메뉴(pages/Devices.tsx)는 라우트에서 빠졌다. 페어링 권한 테스트가 그 화면을 쓰므로 파일은 남긴다.
// =============================================================================

export const APPROVAL_MODE_HELP: Record<ApprovalMode, string> = {
  central: '중앙 관리자·조직 관리자가 승인해야 송출됩니다.',
  group: '그룹 관리자가 승인하면 송출됩니다.',
  auto: '승인 없이 제출 즉시 송출됩니다.',
};
export const STATUS_TOOLTIP =
  '기기 연결과 재생 진단을 함께 확인합니다.\n' +
  '🟢 온라인 : 연결 확인됨 · 현재 재생 오류 없음\n' +
  '⚪ 오프라인 : 90초 넘게 연결 확인 없음\n' +
  '🔴 장애 : 연결 중 재생 오류 또는 진행 정체';
export const SELECT_CLASS = 'rounded border border-line bg-panel-2 px-2.5 py-1.5 text-[14px] text-text focus:border-accent focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed';
// 표의 액션(수정·삭제) 열 — 1280에서 행이 섹션 밖으로 넘쳐 '수정'이 "수"까지만 보이고 '삭제'가
// 아예 잘리던 문제(검수 S5). 가로 스크롤이 생겨도 오른쪽에 붙여 둔다. sticky 셀은 아래 셀이
// 비쳐 보이므로 배경을 깔고, 왼쪽에 옅은 그림자를 둬 "여기부터 잘렸다"는 단서를 남긴다.
export const ACTION_HEAD_CLASS = 'sticky right-0 z-10 bg-panel px-3 py-2.5';
export const ACTION_CELL_CLASS =
  'sticky right-0 z-10 bg-panel group-hover:bg-panel-hover px-3 py-2.5 text-right whitespace-nowrap shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.35)]';
const REFRESH_MS = 15_000;

/** 왼쪽 목록에서 고른 것. 실제 그룹 id 또는 가상 행 둘. */
type Selection = 'all' | 'unassigned' | string;

export function canManageDevices(actor: GroupActor | null, groupId: string | null): boolean {
  if (!actor || (actor.role !== 'Owner' && actor.role !== 'Admin')) return false;
  if (actor.central || !actor.enforced) return true;
  return groupId !== null && actor.groups.some((grant) =>
    grant.groupId === groupId && (grant.role === 'Owner' || grant.role === 'Admin'));
}

export function DeviceGroups({ orgId }: { orgId?: string }): JSX.Element {
  const store = useMemo(() => orgId ? createDeviceGroupsStore(orgId) : useDeviceGroupsStore, [orgId]);
  return <DeviceGroupsContent key={orgId ?? 'current'} store={store} {...(orgId ? { orgId } : {})} />;
}

function DeviceGroupsContent({ store, orgId }: { store: typeof useDeviceGroupsStore; orgId?: string }): JSX.Element {
  const { groups, devices, actor, loading, pending, error, load, createGroup, updateGroup, deleteGroup, assignDevice } = store();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DeviceGroup | null>(null);
  const [pairingOpen, setPairingOpen] = useState(false);
  // 기기 상세·수정은 화면 이동 없이 모달로(대교). 페어링 직후엔 '디바이스 추가' 폼으로 연다.
  const [detail, setDetail] = useState<{ id: string; mode: 'view' | 'edit'; isNew?: boolean } | null>(null);
  const [selection, setSelection] = useState<Selection>('all');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | DiagnosticStatus>('');
  const [timelines, setTimelines] = useState<ScheduleType[]>([]);
  const [deviceError, setDeviceError] = useState<string | null>(null);

  // The shared store starts with loading=false, before the mount effect runs.
  // An existing actor marks loaded data, so refreshes keep editable rows mounted.
  const initialLoading = !actor && !error;
  const busy = initialLoading || loading || pending;
  const canManageGroups = !!actor?.central && (actor.role === 'Owner' || actor.role === 'Admin');
  const canManageAnyDevice = !!actor && (canManageDevices(actor, null) || actor.groups.some((grant) => canManageDevices(actor, grant.groupId)));
  // 삭제 규칙은 서버와 같다: 조직 Owner, 또는 고객사 상세를 보는 운영사(BO).
  const canDeleteDevice = actor?.role === 'Owner' || !!orgId;
  const scope = actor ? { actor, groups, devices } : null;
  const pairable = !!scope && canPair(scope);

  useEffect(() => { void load(); }, [load]);
  // 조용한 폴링 — 온라인 여부·새 페어링 기기를 따라간다. 화면이 숨겨져 있으면 쉰다.
  useEffect(() => {
    const timer = setInterval(() => { if (!document.hidden) void store.getState().refresh(); }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [store]);
  // 할당된 타임라인은 내 조직 화면에서만 — 고객사 상세(orgId)에서는 스코프가 달라 생략.
  useEffect(() => {
    if (orgId) return;
    void api.listScheduleTypes().then(setTimelines).catch(() => setTimelines([]));
  }, [orgId, devices.length]);
  // 그룹 담당자가 한 그룹만 맡으면 그 그룹을 바로 연다.
  useEffect(() => {
    if (!actor || actor.central || actor.groups.length !== 1) return;
    const only = actor.groups[0]!.groupId;
    if (groups.some((group) => group.id === only)) setSelection((current) => current === 'all' ? only : current);
  }, [actor, groups]);
  // 고른 그룹이 사라지면 전체로.
  useEffect(() => {
    if (selection !== 'all' && selection !== 'unassigned' && !loading && actor && !groups.some((group) => group.id === selection)) setSelection('all');
  }, [selection, groups, loading, actor]);

  const diagnostics = useDeviceDiagnostics(devices.map((device) => device.id));
  const timelinesByDevice = useMemo(() => {
    const map = new Map<string, ScheduleType[]>();
    for (const type of timelines) { if (type.deviceId) map.set(type.deviceId, [...(map.get(type.deviceId) ?? []), type]); }
    return map;
  }, [timelines]);
  const statusOf = (device: GroupDevice): DiagnosticStatus => diagnostics.data[device.id]?.status ?? (device.online ? 'online' : 'offline');
  const collator = useMemo(() => new Intl.Collator('ko'), []);
  const groupName = useMemo(() => new Map(groups.map((group) => [group.id, group.name])), [groups]);
  const onlineCount = (list: GroupDevice[]) => list.filter((device) => statusOf(device) === 'online').length;
  const unassigned = useMemo(() => devices.filter((device) => !device.groupId), [devices]);
  const selectedGroup = selection !== 'all' && selection !== 'unassigned' ? groups.find((group) => group.id === selection) ?? null : null;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return devices
      .filter((device) => selection === 'all' ? true : selection === 'unassigned' ? !device.groupId : device.groupId === selection)
      .filter((device) => !q || device.name.toLowerCase().includes(q) || device.id.toLowerCase().includes(q))
      .filter((device) => !statusFilter || statusOf(device) === statusFilter)
      .sort((a, b) => {
        const ga = a.groupId ? groupName.get(a.groupId) ?? '' : null; const gb = b.groupId ? groupName.get(b.groupId) ?? '' : null;
        if ((ga === null) !== (gb === null)) return ga === null ? 1 : -1;
        return collator.compare(ga ?? '', gb ?? '') || collator.compare(a.name, b.name);
      });
  }, [devices, selection, query, statusFilter, groupName, collator, diagnostics.data]);

  const showTimelines = !orgId;
  const showGroupColumn = selection === 'all' || selection === 'unassigned';
  const columnCount = 7 + (showTimelines ? 1 : 0) + (showGroupColumn ? 1 : 0) - 1;


  return (
    <div className={`relative h-full overflow-auto text-text ${SECTION_BODY_GUTTER}`} aria-busy={busy}>
      {/* 도구 줄 — 왼쪽 필터, 오른쪽 동작. */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <label className="contents"><span className="sr-only">디바이스명, 디바이스 ID 검색</span>
            <SearchInput value={query} onChange={setQuery} placeholder="디바이스명, 디바이스 ID 검색" className="w-[260px]" /></label>
          <select aria-label="상태 필터" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as '' | DiagnosticStatus)} className={SELECT_CLASS}>
            <option value="">상태 전체</option><option value="online">온라인</option><option value="fault">장애</option><option value="offline">오프라인</option>
          </select>
        </div>
        <Button disabled={busy} onClick={() => void load()}>새로고침</Button>
      </div>

      {error && <div role="alert" className="mb-4 rounded border border-red/50 bg-red/15 px-3 py-2 text-[15px] text-red">오류: {error}</div>}
      {deviceError && <div role="alert" className="mb-4 rounded border border-red/50 bg-red/15 px-3 py-2 text-[15px] text-red">{deviceError}</div>}
      {diagnostics.error && <p role="status" className="mb-4 text-[13px] text-text-dim">{diagnostics.error} <button type="button" className="underline" onClick={diagnostics.refresh}>진단 다시 확인</button></p>}
      {actor?.central && actor.role === 'Owner' && <GroupScopeSetup orgId={actor.orgId} onApplied={load} />}
      {actor && !actor.enforced && actor.role !== 'Owner' && <p role="status" className="mb-4 text-sm text-text-dim">그룹 권한이 아직 적용되지 않았습니다. 조직 관리자가 적용하면 승인 방식과 접근 범위가 반영됩니다.</p>}
      {/* '~해야 합니다'로 끝내지 않고 행동 버튼을 붙인다(2026-09-17 대교). 이미 구성 관리 안이라
          '구성 관리 열기'는 뺀다 — 자기 자신으로 가는 버튼은 두지 않는다. */}
      {hasNoAssignedGroups(actor) && (
        <NoAssignedGroupsNotice title={NO_GROUP_TITLES.org} onRetry={() => void load()} showWorkspaceLink={false} className="mb-4" />
      )}

      {initialLoading ? <ContentSkeleton variant="table" label="그룹과 디바이스를 불러오는 중…" /> : <fieldset disabled={busy} className="min-w-0 border-0 p-0">
        <div className="grid items-start gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
          {/* 그룹 목록 — 그룹을 만드는 버튼은 그룹 패널 머리에(대교: 동작 버튼은 대상 가까이). */}
          <Panel title={`그룹 (${groups.length})`} bodyClassName="p-0"
            trailing={canManageGroups ? <Button variant="primary" disabled={busy} onClick={() => setCreateOpen(true)}>+ 그룹 만들기</Button> : undefined}>
            <ul className="divide-y divide-line" aria-label="그룹 목록">
              <SelectableRow selected={selection === 'all'} onSelect={() => setSelection('all')}
                mark={<span aria-hidden="true" className="flex h-8 w-8 flex-none items-center justify-center rounded border border-line text-text-dim"><AllGroupsIcon /></span>}
                title="전체" meta={`기기 ${devices.length}대 · 온라인 ${onlineCount(devices)}`} />
              {groups.map((group) => {
                const own = devices.filter((device) => device.groupId === group.id);
                return <SelectableRow key={group.id} selected={selection === group.id} onSelect={() => setSelection(group.id)}
                  mark={<GroupMark group={group} />} title={group.name} tag={APPROVAL_MODE_LABELS[group.approvalMode]}
                  meta={`기기 ${group.deviceCount}대 · 온라인 ${onlineCount(own)} · 관리자 ${group.memberCount}명`}
                  trailing={canManageGroups ? <GroupMenu group={group} canManage={canManageGroups}
                    onEdit={() => setEditTarget(group)}
                    onDelete={async (mode) => { if (!await deleteGroup(group.id, mode)) throw new Error(store.getState().error ?? '그룹을 정리하지 못했습니다.'); if (selection === group.id) setSelection('all'); }} /> : undefined} />;
              })}
              <SelectableRow selected={selection === 'unassigned'} onSelect={() => setSelection('unassigned')} warn={unassigned.length > 0}
                mark={<span aria-hidden="true" className={cn('flex h-8 w-8 flex-none items-center justify-center rounded border text-[13px]', unassigned.length > 0 ? 'border-amber-500/60 text-amber-500' : 'border-line text-text-dim')}>{unassigned.length > 0 ? '!' : '–'}</span>}
                title="미배정" meta={unassigned.length > 0 ? `기기 ${unassigned.length}대 — 그룹을 배정해야 권한이 적용됩니다` : '없음'} />
              {groups.length === 0 && <li className="px-4 py-6 text-center text-[14px] italic text-text-dim">{loading ? '불러오는 중…' : '아직 그룹이 없습니다. 그룹을 만들고 기기를 배정하세요.'}</li>}
            </ul>
          </Panel>

          {/* 고른 그룹의 디바이스 */}
          <section className="min-w-0 rounded-md border border-line bg-panel" aria-label="디바이스 목록">
            <header className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3">
              {/* 그룹 자체의 설정(승인 방식·수정·정리)은 왼쪽 그룹 행의 ⋮ 메뉴에서 — 여기는 읽기용 요약만. */}
              {selectedGroup ? <>
                <GroupMark group={selectedGroup} />
                <h2 className="min-w-0 flex-1 truncate text-[16px] font-semibold">{selectedGroup.name}</h2>
                <span className="rounded border border-line bg-panel-2 px-2 py-0.5 text-[13px] text-text-dim">{APPROVAL_MODE_LABELS[selectedGroup.approvalMode]}</span>
                <span className="rounded border border-line bg-panel-2 px-2 py-0.5 text-[13px] text-text-dim">담당 {selectedGroup.memberCount}명</span>
              </> : <h2 className="flex-1 text-[16px] font-semibold">{selection === 'all' ? '전체 디바이스' : '미배정 디바이스'}</h2>}
              <span className="text-[13px] text-text-dim">{visible.length}대</span>
              {/* 기기를 늘리는 두 버튼은 디바이스 패널 머리에. 고른 그룹이 있으면 페어링이 그 그룹으로 바로 간다. */}
              {canManageAnyDevice && <DeviceCodeClaimButton onClaimed={() => void load()} disabled={busy} />}
              {canManageAnyDevice && <Button variant="primary" disabled={busy || !pairable} title={pairable ? undefined : '기기를 추가할 수 있는 관리자 권한의 그룹이 없습니다.'}
                onClick={() => { if (pairable) setPairingOpen(true); }}>+ 디바이스 추가</Button>}
            </header>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-[14px]">
                <thead className="text-[13px] text-text-dim">
                  <tr>
                    <th className="px-3 py-2.5 font-medium whitespace-nowrap">상태 <InfoTooltip text={STATUS_TOOLTIP} /></th>
                    <th className="px-3 py-2.5 font-medium whitespace-nowrap">디바이스명</th>
                    <th className="px-3 py-2.5 font-medium whitespace-nowrap">운영 환경</th>
                    {showGroupColumn && <th className="px-3 py-2.5 font-medium whitespace-nowrap">그룹</th>}
                    <th className="px-3 py-2.5 font-medium whitespace-nowrap">해상도</th>
                    {showTimelines && <th className="px-3 py-2.5 font-medium whitespace-nowrap">할당된 타임라인</th>}
                    <th className={ACTION_HEAD_CLASS} />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((device) => (
                    <DeviceRowView key={device.id} device={device} groups={groups} actor={actor} busy={busy}
                      status={diagnostics.data[device.id]} online={device.online}
                      // 타임라인이 없어도 빈 배열을 넘겨 열을 유지한다 — null이면 열이 빠져 액션 버튼 위치가 행마다 흔들린다.
                      showGroup={showGroupColumn} timelines={showTimelines ? timelinesByDevice.get(device.id) ?? [] : null}
                      onOpen={() => setDetail({ id: device.id, mode: 'view' })} onEdit={() => setDetail({ id: device.id, mode: 'edit' })}
                      onAssign={(groupId) => { setDeviceError(null); void assignDevice(device.id, groupId); }} />
                  ))}
                  {visible.length === 0 && <tr><td colSpan={columnCount} className="px-3 py-10 text-center text-[14px] text-text-dim">
                    {devices.length === 0 ? (pairable ? '아직 등록된 디바이스가 없습니다. "+ 디바이스 추가"로 첫 기기를 연결하세요.' : '아직 등록된 디바이스가 없습니다.')
                      : query || statusFilter ? '검색 결과가 없습니다.' : selection === 'unassigned' ? '미배정 기기가 없습니다.' : '이 그룹에 배정된 기기가 없습니다.'}
                  </td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </fieldset>}

      {createOpen && canManageGroups && (
        <GroupFormModal mode="create" busy={pending} error={error} canSetPolicy={!!actor?.central && actor.role === 'Owner'}
          onClose={() => setCreateOpen(false)}
          onSubmit={async (input) => { const ok = await createGroup(input); if (ok) setCreateOpen(false); return ok; }} />
      )}
      {editTarget && canManageGroups && (
        <GroupFormModal mode="edit" group={editTarget} busy={pending} error={error} canSetPolicy={!!actor?.central && actor.role === 'Owner'}
          onClose={() => setEditTarget(null)}
          onSubmit={async (input) => {
            const ok = await updateGroup(editTarget.id, { name: input.name, approvalMode: input.approvalMode, policyVersion: editTarget.policyVersion,
              accentColor: input.accentColor ?? null, logoDataUrl: input.logoDataUrl ?? null });
            if (ok) setEditTarget(null); return ok;
          }} />
      )}
      {pairingOpen && scope && pairable && (
        <NewDevicePairing key={`${selection}:${groups.length}`} scope={scope}
          initialGroupId={selectedGroup && pairingGroups(scope).some((group) => group.id === selectedGroup.id) ? selectedGroup.id : null}
          onClose={() => setPairingOpen(false)}
          onPaired={(id) => { setPairingOpen(false); void load(); setDetail({ id, mode: 'edit', isNew: true }); }} />
      )}
      {detail && (
        <DeviceDetailModal key={detail.id} deviceId={detail.id} initialMode={detail.mode} isNew={detail.isNew ?? false}
          onClose={() => setDetail(null)} onChanged={() => void load()} />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// 조각들
// -----------------------------------------------------------------------------

/** '전체' 행 마크 — 네 칸 격자(모든 그룹). */
export function AllGroupsIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="2" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.4" />
      <rect x="9" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.4" />
      <rect x="2" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.4" />
      <rect x="9" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

export function SelectableRow({ selected, warn = false, mark, title, tag, badge, meta, trailing, onSelect }: {
  selected: boolean; warn?: boolean; mark: JSX.Element; title: string;
  /** 제목 오른쪽 경고 배지(장애 기기 수 등). */
  badge?: string | undefined;
  /** 제목 옆 짧은 표식(승인 방식 등). 메타 줄이 길어져 말줄임되는 걸 막기 위해 분리했다. */
  tag?: string | undefined;
  /** 두 줄까지 감싼다 — '…' 말줄임 대신 다 보이게(대교). */
  meta: string; trailing?: JSX.Element | undefined; onSelect: () => void;
}): JSX.Element {
  // 선택 버튼과 ⋮ 메뉴는 형제 — 버튼 안에 버튼을 넣지 않는다.
  return (
    <li className={cn('flex items-center gap-1 pr-2 transition-colors', selected ? 'bg-accent-soft' : 'hover:bg-panel-hover', warn && !selected && 'bg-amber-500/5')}>
      <button type="button" aria-pressed={selected} onClick={onSelect} className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-left">
        {mark}
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className={cn('min-w-0 truncate text-[15px] font-medium', selected && 'text-accent')}>{title}</span>
            {/* 11px → 12px (검수 C2 — 10~11px 남용). 배지 높이는 leading으로 그대로 유지. */}
            {tag && <span className="flex-none rounded border border-line bg-panel-2 px-1.5 text-[12px] leading-5 text-text-dim">{tag}</span>}
            {badge && <span className="flex-none rounded-full bg-red px-1.5 text-[12px] font-semibold leading-5 text-white" title="장애 기기">{badge}</span>}
          </span>
          <span className="block whitespace-normal text-[12px] leading-snug text-text-dim">{meta}</span>
        </span>
      </button>
      {trailing}
    </li>
  );
}

/** 그룹 행의 ⋮ — 수정… / 그룹 삭제. 승인 방식은 그룹 수정 모달 안에서 조직 Owner만 바꾼다(2026-09-15). */
type ArchiveImpact = { media: number; playlists: number; notices: number; devices: number; members: number };

export function GroupMenu({ group, canManage, write, onEdit, onDelete }: {
  group: DeviceGroup; canManage: boolean;
  /** 쓰기 게이트(대행 읽기 전용·권한). 주면 메뉴 항목을 숨기지 않고 비활성 + 이유로 둔다(검수 S1·S11). */
  write?: WriteGate | undefined;
  /** 그룹 수정 모달(이름·승인 방식·로고·관리자). */
  onEdit: () => void;
  /** mode 없이 부르면 빈 그룹 삭제. 남은 것이 있으면 처리 방식을 함께 보낸다. */
  onDelete: (mode?: 'transfer' | 'purge') => Promise<void>;
}): JSX.Element {
  const [confirmDel, setConfirmDel] = useState(false);
  const [impact, setImpact] = useState<ArchiveImpact | null>(null);
  const [busy, setBusy] = useState(false);

  // 2026-09-18 대교: "삭제 버튼이 완전 비활성인데, 미디어·라이브러리·등록된 기기 등이 모두
  // 삭제된다는 안내와 함께 삭제 시도를 할 수 있어야 할 것." 남은 것이 있다고 버튼을 죽이지
  // 않는다 — 무엇이 정리되는지 보여 주고 처리 방식을 고르게 한다. 다만 진행 중 승인은 사람이
  // 내려야 할 결정이라 그대로 막는다(서버도 같은 이유로 막는다).
  const pending = group.openRequestCount ?? 0;
  const occupied = !!(group.deviceCount || group.memberCount || group.contentCount);
  const gate = write ?? writeGate(canManage, '');
  if (gate.hidden || (!write && !canManage)) return <></>;
  const del = gate.and(!pending, `진행 중 승인 ${pending}건을 먼저 처리해 주세요.`);

  useEffect(() => {
    if (!confirmDel || !occupied) return;
    let alive = true;
    void fetch(`${API_BASE}/api/device-groups/${encodeURIComponent(group.id)}/archive-impact`, { credentials: 'include' })
      .then(response => response.ok ? response.json() as Promise<ArchiveImpact> : null)
      .then(value => { if (alive && value) setImpact(value); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [confirmDel, occupied, group.id]);

  const run = async (mode?: 'transfer' | 'purge') => {
    setBusy(true);
    try { await onDelete(mode); setConfirmDel(false); } finally { setBusy(false); }
  };
  const lines = impact && [
    impact.media ? `미디어 ${impact.media}개` : null,
    impact.notices ? `슬라이드 ${impact.notices}개` : null,
    impact.playlists ? `플레이리스트 ${impact.playlists}개` : null,
    impact.devices ? `등록된 기기 ${impact.devices}대` : null,
    impact.members ? `담당 관리자 ${impact.members}명` : null,
  ].filter((value): value is string => !!value);

  return <>
    <ToolbarMenu glyph="⋮" title={`${group.name} 메뉴`} align="right" className="h-8 w-8 flex-none border-transparent bg-transparent text-text-dim hover:bg-panel-2 hover:text-text">
      {(write || canManage) && <MenuRow label="수정…" disabled={gate.blocked} {...(gate.reason ? { title: gate.reason } : {})} onClick={onEdit} />}
      {(write || canManage) && <>
        <MenuDivider />
        <MenuRow label="그룹 삭제" danger disabled={del.blocked} title={del.reason ?? '그룹 삭제'}
          onClick={() => { setImpact(null); setConfirmDel(true); }} />
      </>}
    </ToolbarMenu>
    {confirmDel && !occupied && (
      <ConfirmModal title="그룹 삭제" confirmLabel="삭제"
        message={`'${group.name}' 그룹을 삭제합니다. 기기·관리자·콘텐츠가 하나도 없는 빈 그룹이라 되돌릴 내용은 없습니다.`}
        onCancel={() => setConfirmDel(false)} onConfirm={() => run()} />
    )}
    {confirmDel && occupied && (
      <ModalShell title="그룹 삭제" onClose={() => setConfirmDel(false)} size="md"
        footer={<>
          <Button onClick={() => setConfirmDel(false)} disabled={busy}>취소</Button>
          <Button onClick={() => void run('transfer')} disabled={busy}>조직 관리자 자료로 옮기고 삭제</Button>
          <Button variant="danger" onClick={() => void run('purge')} disabled={busy}>함께 삭제</Button>
        </>}>
        <p className="text-[14px]">‘{group.name}’ 그룹에 다음이 남아 있습니다.</p>
        {lines === null ? (
          <p className="text-[13px] text-text-dim">남은 항목을 확인하는 중…</p>
        ) : lines.length === 0 ? (
          <p className="text-[13px] text-text-dim">정리할 항목이 없습니다.</p>
        ) : (
          <ul className="list-disc pl-5 text-[14px]">{lines.map(line => <li key={line}>{line}</li>)}</ul>
        )}
        <p className="text-[13px] text-text-dim leading-[1.6]">
          <b>조직 관리자 자료로 옮기고 삭제</b> — 콘텐츠는 조직 관리자 자료로 옮기고, 기기는 그룹에서만 뗍니다(등록은 유지).<br />
          <b>함께 삭제</b> — 위 콘텐츠와 기기를 실제로 지웁니다. 기기는 현장에서 다시 등록해야 하며 <b>되돌릴 수 없습니다.</b>
        </p>
      </ModalShell>
    )}
  </>;
}

/** 그룹 로고(있으면) 또는 악센트 컬러 점. 둘 다 없으면 이름 첫 글자. */
export function GroupMark({ group }: { group: { name: string; accentColor?: string | null; logoDataUrl?: string | null } }): JSX.Element {
  const accent = group.accentColor ?? undefined;
  if (group.logoDataUrl) {
    return <span className="flex h-8 w-8 flex-none items-center justify-center overflow-hidden rounded border border-line bg-white" style={accent ? { borderColor: accent } : undefined}>
      <img src={group.logoDataUrl} alt="" className="max-h-full max-w-full object-contain" />
    </span>;
  }
  return <span aria-hidden="true" className="flex h-8 w-8 flex-none items-center justify-center rounded text-[13px] font-semibold text-white"
    style={{ backgroundColor: accent ?? 'var(--color-text-dimmer, #9a9a9a)' }}>{group.name.trim().charAt(0)}</span>;
}

export function DeviceRowView({ device, groups, actor, busy, status, online, showGroup, timelines, onOpen, onEdit, onAssign }: {
  device: GroupDevice; groups: DeviceGroup[]; actor: GroupActor | null; busy: boolean;
  status: ReturnType<typeof useDeviceDiagnostics>['data'][string] | undefined; online: boolean;
  showGroup: boolean; timelines: ScheduleType[] | null;
  onOpen: () => void; onEdit: () => void; onAssign: (groupId: string | null) => void;
}): JSX.Element {
  // 일시적 차단(대행 읽기 전용)은 비활성 + 이유, 역할상 영구 불가(내 그룹 아님·Owner 전용)는 숨긴다.
  const write = useWriteGate();
  const canMove = canManageDevices(actor, device.groupId);
  const moveWrite = write.and(canMove, '이 기기를 관리할 권한이 없습니다.', { hide: true });
  const liveDevice = { ...device, online: online && status?.status !== 'offline' };
  const powerBlocked = devicePowerBlockReason(liveDevice);
  const canPower = !busy && !write.blocked && canMove && powerBlocked === null;
  // 전원 버튼은 HIKVISION 패널로 분류된 기기에만 존재한다. 웹·일반 Android 는 버튼 자체가 없다.
  const powerVisible = showsPowerCard(liveDevice);
  const timelineLabel = !timelines || timelines.length === 0 ? '-' : timelines.length === 1 ? timelines[0]!.name : `${timelines[0]!.name} 외 ${timelines.length - 1}개`;
  // 비활성 사유는 기본 title 대신 aria-description(HoverTipLayer가 말풍선) — 긴 문구가 잘리지 않게(2026-10-01 대교).
  // 켜진 버튼의 짧은 설명은 title 그대로.
  const powerReason = powerVisible && !canPower ? (powerBlocked ?? moveWrite.reason) : undefined;
  const editReason = busy || moveWrite.blocked ? moveWrite.reason : undefined;
  return (
    <tr className="group border-t border-line hover:bg-panel-hover">
      <td className="px-3 py-2.5 whitespace-nowrap">
        {status ? <DeviceDiagnosticsBadge diagnostic={status} />
          : <span className="inline-flex items-center gap-1.5"><span className={cn('h-2 w-2 rounded-full', online ? 'bg-green' : 'bg-text-faint')} />{online ? '온라인' : '오프라인'}</span>}
      </td>
      <td className="max-w-[240px] px-3 py-2.5">
        <button type="button" onClick={onOpen} className="block max-w-full truncate text-left text-accent hover:underline">{device.name}</button>
        {device.siteLabel && <span className="block truncate text-[12px] text-text-dim">{device.siteLabel}</span>}
      </td>
      <td className="px-3 py-2.5"><DeviceRuntimeBadge device={device} compact /></td>
      {showGroup && <td className="px-3 py-2.5">
        {/* 권한이 없어도 셀렉트를 텍스트로 바꾸지 않는다 — 열 폭과 조작부 위치가 행마다 달라진다. */}
        <select aria-label={`${device.name} 그룹 배정`} value={device.groupId ?? ''} {...moveWrite.props} disabled={busy || moveWrite.blocked} className={SELECT_CLASS}
          onChange={(event) => { const groupId = event.target.value || null; if (canManageDevices(actor, groupId)) onAssign(groupId); }}>
          <option value="" disabled={!canManageDevices(actor, null)}>(미배정)</option>
          {groups.map((group) => <option key={group.id} value={group.id} disabled={!canManageDevices(actor, group.id)}>{group.name}</option>)}
          {device.groupId && !groups.some((group) => group.id === device.groupId) && <option value={device.groupId} disabled>현재 그룹 (조회 불가)</option>}
        </select>
      </td>}
      <td className="px-3 py-2.5 whitespace-nowrap">{device.resolutionW && device.resolutionH ? `${device.resolutionW} × ${device.resolutionH}` : '자동'}</td>
      {timelines !== null && <td className="max-w-[200px] px-3 py-2.5"><span className="block truncate" title={timelines.map((type) => type.name).join('\n')}>{timelineLabel}</span></td>}
      <td className={ACTION_CELL_CLASS}>
        {/* 전원 버튼이 없는 행은 빈 칸 없이 미리보기와 수정을 붙인다. */}
        <div className={cn('inline-grid items-center gap-1.5 align-middle max-md:flex max-md:flex-wrap max-md:justify-end max-md:[&>*]:w-auto', powerVisible ? 'grid-cols-[80px_148px_56px]' : 'grid-cols-[80px_56px]')}>
        <button type="button" onClick={() => window.open(playerPreviewUrl(device.id), '_blank', 'noopener')} title="이 디바이스의 현재 편성을 새 창에서 미리보기"
          className="inline-flex h-9 w-full items-center justify-center rounded border border-line px-3 text-[13px] hover:border-line-strong hover:bg-panel">미리보기</button>
        {/* 미확인·미지원·오프라인은 전원 설정 진입을 허용하지 않는다. */}
        {powerVisible && <button type="button" onClick={() => { if (canPower) onOpen(); }} disabled={!canPower}
          {...(powerReason ? { 'aria-description': powerReason } : { title: '이 디바이스의 전원 스케줄과 화면 끄기·켜기' })}
          className={cn('inline-flex h-9 w-full items-center justify-center gap-1.5 rounded border px-3 text-[13px] disabled:cursor-not-allowed disabled:opacity-40',
            canPower && 'hover:border-line-strong hover:bg-panel',
            device.screenOffAt && canPower ? 'border-red/50 text-red' : 'border-line')}>
          <Icon name="power" size={14} />전원{canPower && (device.screenOffAt ? ' · 화면 꺼짐' : device.powerSchedule?.enabled ? ' · 예약' : '')}
        </button>}
        {/* 역할상 불가한 수정은 보이지 않게 하되 자리는 남긴다(invisible) — 행마다 열이 흔들리지 않게.
            삭제는 표에서 뺐다 — 그룹 안 표라 '이 그룹에서 빼기'로 읽히는데 실제로는 기기를 없앤다.
            수정 화면 맨 아래 위험 구역으로 옮겼다(2026-09-18 대교). */}
        <button type="button" onClick={onEdit} disabled={busy || moveWrite.blocked} aria-label={`${device.name} 수정`}
          {...(editReason ? { 'aria-description': editReason } : {})}
          aria-hidden={moveWrite.hidden || undefined} tabIndex={moveWrite.hidden ? -1 : undefined}
          className={cn('inline-flex h-9 w-full items-center justify-center rounded border border-line px-3 text-[13px] hover:border-line-strong hover:bg-panel disabled:cursor-not-allowed disabled:opacity-40', moveWrite.hidden && 'invisible pointer-events-none')}>수정</button>
        </div>
      </td>
    </tr>
  );
}

const LOGO_MAX_BYTES = 300 * 1024;
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];

export function GroupFormModal({ mode, group, busy, error, onClose, onSubmit, members, canSetPolicy = true }: {
  mode: 'create' | 'edit';
  group?: DeviceGroup;
  /** 승인 방식은 조직 Owner만 바꾼다. false면 라디오를 읽기 전용으로 보인다(중앙 관리자가 이름·로고만 고칠 때). */
  canSetPolicy?: boolean;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (input: { name: string; approvalMode: ApprovalMode } & GroupBranding) => Promise<boolean>;
  /** 구성 관리 > 현황에서만: 관리자 지정 섹션. 조직 관리자 목록과 현재 배정을 받아 체크·역할을 고르고, 저장 시 onChange로 넘긴다. */
  members?: GroupMemberPicker | undefined;
}): JSX.Element {
  const [name, setName] = useState(group?.name ?? '');
  const [approvalMode, setApprovalMode] = useState<ApprovalMode>(group?.approvalMode ?? 'central');
  const [accent, setAccent] = useState<string>(group?.accentColor ?? '');
  const [logo, setLogo] = useState<string | null>(group?.logoDataUrl ?? null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const valid = name.trim().length > 0 && name.trim().length <= 100 && (accent === '' || /^#[0-9a-fA-F]{6}$/.test(accent));

  function submit(): void {
    if (!valid || busy) return;
    setSubmitted(true);
    // 생성 시엔 비어 있는 브랜딩을 보내지 않는다(요청 본문 최소화). 수정 시엔 null로 지움을 명시한다.
    void onSubmit({ name: name.trim(), approvalMode: canSetPolicy ? approvalMode : (group?.approvalMode ?? approvalMode),
      ...(accent ? { accentColor: accent } : mode === 'edit' ? { accentColor: null } : {}),
      ...(logo ? { logoDataUrl: logo } : mode === 'edit' ? { logoDataUrl: null } : {}) });
  }

  function pickLogo(file: File | undefined): void {
    setLogoError(null);
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) { setLogoError('PNG · JPEG · WebP · SVG 이미지만 올릴 수 있습니다.'); return; }
    if (file.size > LOGO_MAX_BYTES) { setLogoError('로고 파일은 300KB 이하로 올려 주세요.'); return; }
    const reader = new FileReader();
    reader.onload = () => setLogo(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => setLogoError('파일을 읽지 못했습니다.');
    reader.readAsDataURL(file);
  }

  // 폭은 ModalShell의 md(560) 한 단계로 — 높이 클램프·본문 스크롤·고정 푸터는 셸이 맡는다(검수 S3).
  // 제출·취소는 footer로 넘겨 1280×800에서도 잘리지 않게 한다. 푸터 버튼은 form 밖이라
  // type=submit에 기대지 않고 같은 submit()을 직접 부른다(Enter 제출은 form의 onSubmit이 받는다).
  return (
    <ModalShell title={mode === 'create' ? '그룹 만들기' : '그룹 수정'} onClose={() => { if (!busy) onClose(); }} size="md"
      footer={<>
        <Button type="button" disabled={busy} onClick={onClose}>취소</Button>
        <Button type="button" variant="primary" disabled={busy || !valid} onClick={submit}>{mode === 'create' ? '그룹 생성' : '저장'}</Button>
      </>}>
      <form className="flex flex-col gap-4" onSubmit={(event) => { event.preventDefault(); submit(); }}>
        <Field label="그룹 이름" placeholder="예: 강원디자인진흥원" value={name} autoFocus
          onChange={(event) => setName(event.target.value)} />

        <fieldset className="flex flex-col gap-2 border-0 p-0" disabled={!canSetPolicy}>
          <legend className="mb-1 text-[13px] font-medium text-text-dim">승인 방식 {!canSetPolicy && <span className="font-normal text-text-dimmer">— 조직 관리자만 바꿀 수 있습니다</span>}</legend>
          {(Object.keys(APPROVAL_MODE_LABELS) as ApprovalMode[]).map((option) => (
            <label key={option} className={`flex items-start gap-3 rounded border px-3 py-2 ${canSetPolicy ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'} ${approvalMode === option ? 'border-accent bg-accent-soft' : 'border-line hover:border-line-strong'}`}>
              <input type="radio" name="group-approval-mode" value={option} checked={approvalMode === option}
                onChange={() => setApprovalMode(option)} className="mt-1" />
              <span className="flex flex-col">
                <span className="text-[15px] font-medium">{APPROVAL_MODE_LABELS[option]}</span>
                <span className="text-[13px] text-text-dim">{APPROVAL_MODE_HELP[option]}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium text-text-dim">로고</span>
            <div className="flex items-center gap-3">
              <GroupMark group={{ name: name || group?.name || '?', accentColor: accent || null, logoDataUrl: logo }} />
              <label className="cursor-pointer rounded border border-line px-3 py-1.5 text-[13px] hover:border-line-strong">
                {logo ? '로고 바꾸기' : '로고 올리기'}
                <input type="file" accept={LOGO_TYPES.join(',')} className="sr-only" aria-label="그룹 로고 파일"
                  onChange={(event) => { pickLogo(event.target.files?.[0]); event.target.value = ''; }} />
              </label>
              {logo && <Button type="button" onClick={() => setLogo(null)}>제거</Button>}
            </div>
            <span className="text-[12px] text-text-dimmer">PNG · JPEG · WebP · SVG, 300KB 이하</span>
            {logoError && <p role="alert" className="text-[12px] text-red">{logoError}</p>}
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[13px] font-medium text-text-dim" htmlFor="group-accent-color">악센트 컬러</label>
            <div className="flex items-center gap-2">
              <input id="group-accent-color" type="color" aria-label="악센트 컬러 선택" value={accent || '#888888'}
                onChange={(event) => setAccent(event.target.value)} className="h-9 w-12 cursor-pointer rounded border border-line bg-panel-2 p-0.5" />
              <input type="text" aria-label="악센트 컬러 값" value={accent} placeholder="#RRGGBB" spellCheck={false}
                onChange={(event) => setAccent(event.target.value.trim())}
                className={`w-[120px] ${SELECT_CLASS} font-mono ${accent && !/^#[0-9a-fA-F]{6}$/.test(accent) ? 'border-red' : ''}`} />
              {accent && <Button type="button" onClick={() => setAccent('')}>지움</Button>}
            </div>
            <span className="text-[12px] text-text-dimmer">그룹 표시·템플릿 강조색에 쓰입니다.</span>
          </div>
        </div>

        {members && <GroupMemberSection picker={members} />}
        {mode === 'create' && <p className="text-[13px] text-text-dim">기기는 만든 뒤 이 그룹을 고르고 "+ 디바이스 추가"로 바로 연결하거나, 목록에서 그룹 이동으로 배정합니다.</p>}
        {submitted && error && <p role="alert" className="text-[13px] text-red">{error}</p>}
      </form>
    </ModalShell>
  );
}

// -----------------------------------------------------------------------------
// 관리자 지정 섹션 — 그룹 모달 안에서 이 그룹을 맡을 관리자를 고른다(구성 관리 전용).
// 그룹 안 역할은 따로 없다 — 조직 권한 그대로 이 그룹에서 활동한다(대교 결정 2026-09-15). 서버 배정의 role은 조직 권한과 같은 값으로 보낸다.
// -----------------------------------------------------------------------------
export type GroupMemberRole = 'Admin' | 'Editor' | 'Viewer';
export type GroupMemberCandidate = { userId: string; name: string | null; loginId: string | null; email: string; role: string; central: boolean };
export type GroupMemberPicker = {
  candidates: GroupMemberCandidate[];
  /** userId → 이 그룹에서의 역할. 없으면 미배정. */
  value: Map<string, GroupMemberRole>;
  onChange: (next: Map<string, GroupMemberRole>) => void;
};
const GROUP_MEMBER_ROLES: GroupMemberRole[] = ['Admin', 'Editor', 'Viewer'];
export function allowedGroupRoles(orgRole: string): GroupMemberRole[] {
  const index = GROUP_MEMBER_ROLES.indexOf(orgRole as GroupMemberRole);
  return index < 0 ? [] : GROUP_MEMBER_ROLES.slice(index);
}

function GroupMemberSection({ picker }: { picker: GroupMemberPicker }): JSX.Element {
  const assignable = picker.candidates.filter((member) => member.role !== 'Owner' && !member.central && allowedGroupRoles(member.role).length > 0);
  const skipped = picker.candidates.length - assignable.length;
  return (
    <fieldset className="flex flex-col gap-2 border-0 p-0">
      <legend className="mb-1 text-[13px] font-medium text-text-dim">관리자 <span className="font-normal text-text-dimmer">({picker.value.size}명 지정)</span></legend>
      {assignable.length === 0 && <p className="text-[13px] text-text-dim">지정할 수 있는 관리자가 없습니다. 현황에서 관리자를 먼저 추가하세요.</p>}
      <ul className="max-h-[220px] divide-y divide-line overflow-auto rounded border border-line">
        {assignable.map((member) => {
          const current = picker.value.get(member.userId);
          const roles = allowedGroupRoles(member.role);
          const label = member.name ?? member.loginId ?? member.email;
          return (
            <li key={member.userId} className="flex items-center gap-3 px-3 py-2 text-[14px]">
              <input type="checkbox" aria-label={`${label} 관리자 지정`} checked={!!current}
                onChange={(event) => { const next = new Map(picker.value); if (event.target.checked) next.set(member.userId, roles[0]!); else next.delete(member.userId); picker.onChange(next); }} />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{label}</span>
                <span className="block truncate text-[12px] text-text-dim">{member.email}</span>
              </span>
              <span className="flex-none text-[13px] text-text-dim">{roleLabelOf(member.role)}</span>
            </li>
          );
        })}
      </ul>
      {skipped > 0 && <p className="text-[12px] text-text-dimmer">조직 관리자·중앙 관리자 {skipped}명은 모든 그룹을 관리하므로 목록에서 제외했습니다.</p>}
    </fieldset>
  );
}
const roleLabelOf = (role: string) => ROLE_LABELS[role as keyof typeof ROLE_LABELS] ?? role;
